import { useMcpTool } from 'webmcp-react'
import { uiStore } from 'shared/store/uiStore'
import { EnumUserRoles } from 'entities/Users/model/types'
import { useEntitiesContext } from 'entities/EntitiesExport'
import {
  navigateSchema,
  resolveNavPath,
  NAVIGATE_DESCRIPTION,
} from 'entities/WebMcp/model/navAreas'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'
import { navGate } from 'entities/WebMcp/model/roleGating'

/**
 * Registers the WebMCP navigation tool. Renders nothing.
 * Drives the app via the global uiStore, alongside the data tools in ContactsTools.
 *
 * Targets the six top-level areas plus the deeper, deep-linkable screens
 * declared in NAV_TARGETS. Destinations without a URL should get a real route
 * before they become navigate targets.
 */
export const NavigationTools = () => {
  const { usersStore, featureFlagsStore } = useEntitiesContext()

  useMcpTool({
    name: 'navigate',
    description: NAVIGATE_DESCRIPTION,
    input: navigateSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ area }) => {
      try {
        // Role gate: withhold admin-only destinations and relay the approved
        // escalation copy instead of navigating to a screen the user can't use.
        // Fail closed if the role has not hydrated yet (treat as least-privileged),
        // matching the command-menu builder, so an early prompt can't slip through.
        const role = usersStore.user?.role ?? EnumUserRoles.VIEW_ONLY
        const escalation = navGate(role, area)
        if (escalation) return okResult({ navigated: false, escalation })

        // RCS has no role gate, only a feature gate: the route loads for anyone,
        // but the settings menu hides it, so sending a user there off-menu would
        // land them on a screen they cannot otherwise reach.
        if (area === 'RCS' && !featureFlagsStore.canManageRCS) {
          return okResult({
            navigated: false,
            escalation: 'RCS is not enabled on this account.',
          })
        }

        uiStore.changeRouteViaVue({ path: resolveNavPath(area) })
        return okResult({ navigated: true, area })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}
